<template>
    <div class="row justify-content-center">
        <div class="col-4">
            <div class="card card-gg card-summary">
                <div class="card-body">
                    <div v-if="isLoading" class="text-center py-4">
                        <div class="spinner-border" role="status">
                            <span class="sr-only">Loading...</span>
                        </div>
                        <p class="mt-2">Loading checkout...</p>
                    </div>
                    <div id="checkout"></div>
                    <div
                        v-if="errorMessage"
                        role="alert"
                        class="invalid-feedback d-block"
                        v-html="errorMessage"
                    ></div>
                </div>
            </div>
        </div>
        <div class="col-4">
            <div class="card card-gg card-summary">
                <div class="card-body">
                    <h4>Order Summary</h4>
                    <table class="table" v-if="cart">
                        <thead>
                        <tr>
                            <th colspan="2">Team / Venue (A-Z)</th>
                            <th>$ Amount</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr
                            v-for="team in cart.teams"
                            :key="team.team.id"
                            v-if="team.total > 0"
                        >
                            <td>
                                <img
                                    :alt="team.team.name"
                                    :src="team.team.photo_url"
                                    height="24"
                                    width="24"
                                    class="mr-1"
                                />
                            </td>
                            <td>{{ team.team.name }}</td>
                            <td class="team-price">{{ formatMoney(team.total) }}</td>
                        </tr>
                        </tbody>
                    </table>
                    <div v-if="cart" class="team-count">
                        {{ cart.team_count }} Teams | {{ cart.ready_rows_count }}
                        {{ cart.ready_rows_count === 1 ? 'Asset' : 'Assets' }}
                    </div>
                    <div v-if="cart" class="total-cost">
                        Total Cost: <span class="money">{{ formatMoney(cart.total) }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            isLoading: true,
            errorMessage: '',
            stripe: null,
            checkout: null,
            cart: null,
            isLoadingCart: true,
        };
    },

    props: {
        stripeKey: String,
    },

    async mounted() {
        await this.loadCart();
        await this.initializeEmbeddedCheckout();
        this.setupEventListeners();
    },

    beforeDestroy() {
        this.cleanupEventListeners();
        if (this.checkout) {
            this.checkout.destroy();
        }
    },

    methods: {
        async loadCart() {
            try {
                const response = await axios.get('/api/checkout/cart');
                this.cart = response.data.data;
                this.isLoadingCart = false;
            } catch (error) {
                console.error('Failed to load cart:', error);
                this.errorMessage = 'Failed to load cart data.';
                this.isLoadingCart = false;
            }
        },

        async initializeEmbeddedCheckout() {
            try {
                this.isLoading = true;

                // Initialize Stripe
                this.stripe = Stripe(this.stripeKey);

                // Create the embedded checkout with appearance customization
                this.checkout = await this.stripe.initEmbeddedCheckout({
                    fetchClientSecret: this.fetchClientSecret,
                });

                // Mount the checkout form
                this.checkout.mount('#checkout');

                this.isLoading = false;
            } catch (error) {
                console.error('Failed to initialize embedded checkout:', error);
                this.errorMessage = 'Failed to initialize checkout. Please refresh and try again.';
                this.isLoading = false;
            }
        },

        async fetchClientSecret() {
            const response = await axios.post('/api/checkout/session');
            return response.data.data.client_secret;
        },

        formatMoney(amount) {
            if (typeof money === 'function') {
                return money(amount);
            }
            return '$' + (amount).toFixed(2);
        },

        setupEventListeners() {
            Bus.$on('cart:updated', this.handleCartUpdate);
            this._cartRefreshHandler = () => this.loadCart();
            window.addEventListener('cart:refresh', this._cartRefreshHandler);
        },

        cleanupEventListeners() {
            Bus.$off('cart:updated', this.handleCartUpdate);
            if (this._cartRefreshHandler) {
                window.removeEventListener('cart:refresh', this._cartRefreshHandler);
            }
        },

        async handleCartUpdate(cartData) {
            this.cart = cartData;
            // Reinitialize checkout if cart changed
            if (this.checkout) {
                this.checkout.destroy();
                await this.initializeEmbeddedCheckout();
            }
        },
    },
};
</script>

<style scoped>
/* Container around the Stripe embedded checkout */
#checkout {
    padding: 20px;
}

/* Loading state */
.spinner-border {
    width: 3rem;
    height: 3rem;
    color: #28a745;
}

/* Card styles */
.card-gg {
    border: none;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border-radius: 8px;
}

.card-gg .card-header {
    background: #fff;
    border-bottom: 1px solid #e9ecef;
    font-weight: 600;
    font-size: 18px;
    padding: 16px 20px;
}

.card-gg .card-body {
    padding: 20px;
}

/* Summary card */
.card-summary {
    position: sticky;
    top: 20px;
}

.card-summary h4 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #333;
}

.card-summary .table {
    margin-bottom: 0;
    font-size: 14px;
}

.card-summary .table th {
    border-top: none;
    font-weight: 500;
    color: #6c757d;
    padding: 8px 0;
}

.card-summary .table td {
    padding: 10px 0;
    vertical-align: middle;
    border-color: #f0f0f0;
}

.team-price {
    text-align: right;
    font-weight: 500;
}

.team-count {
    font-size: 13px;
    color: #6c757d;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #e9ecef;
}

.total-cost {
    font-weight: 700;
    font-size: 20px;
    margin: 16px 0;
    padding: 16px 0;
    border-top: 2px solid #e9ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.money {
    color: #28a745;
    font-size: 24px;
}

/* Error message */
.invalid-feedback {
    background: #fff5f5;
    border: 1px solid #df1b41;
    border-radius: 4px;
    padding: 12px 16px;
    margin-top: 16px;
    color: #df1b41;
}
</style>
